import { Creators as PlayerCreators } from 'reducers/Player'
import { Creators as PlayHistoryCreators } from 'reducers/PlayHistory'
import { AudioService } from 'services'
import { storeHelpers, browserHelpers } from 'utils'
import { sampleSongUrl } from 'config'
import cloneDeep from 'lodash/cloneDeep'
import { trackEvent } from 'utils/segmentService'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import type { AppDispatch, RootState } from 'types/store'

export type PlaySource = {
  source?: string
  trigger?: string
}

const trackPlaySong = (
  song: any,
  {
    source = PLAY_SOURCES.UNKNOWN,
    trigger = PLAY_TRIGGERS.UNKNOWN
  }: PlaySource = {}
) => {
  trackEvent('Play Song', {
    id: String(song.trackid),
    artist: song.artist ? String(song.artist) : '',
    title: song.title ? String(song.title) : '',
    source,
    trigger
  })
}

export const playSampleSongOnSafari = () => (dispatch: AppDispatch) => {
  if (browserHelpers.shouldRunSampleAudio) {
    storeHelpers.asyncGetStoreState().then(({ player }: any) => {
      const { sampleSongFlag } = player
      if (!sampleSongFlag) {
        dispatch(PlayerCreators.updatePlayerState(true))
        dispatch(PlayerCreators.selectSong({ mediaURL: sampleSongUrl }))
        dispatch(PlayerCreators.updateSampleSongFlag(true))
      }
    })
  }
}

export const playSong =
  (song: any, playSource?: PlaySource) =>
  (dispatch: AppDispatch, getState?: () => RootState) => {
    trackPlaySong(song, playSource)
    dispatch(PlayerCreators.updatePendingPlayTrigger(null))

    const currentSong = (getState?.().player as any)?.song
    const isSameLoadedSong =
      Boolean(currentSong?.mediaURL) &&
      currentSong?.trackid !== undefined &&
      String(currentSong.trackid) === String(song.trackid)

    if (isSameLoadedSong) {
      // The <audio> element already has this track loaded; fetching a new token
      // returns the same mediaURL, so the source never reloads and `loadeddata`
      // never fires, leaving isLoading stuck and the progress bar frozen at 0.
      // Restart in place instead: seek to 0 and play.
      dispatch(PlayerCreators.seekTimeRequest(0))
      dispatch(PlayerCreators.updatePlayerState(true))
      return
    }

    dispatch(PlayerCreators.updateCurrentTime(0))
    dispatch(PlayerCreators.updateLoadingPlayer(true))
    dispatch(playSampleSongOnSafari() as any)
    AudioService.getToken(String(song.trackid))
      .then((response: any) => {
        const token = response.data.token
        const mediaURL = AudioService.getAudioStreamURL(token)
        const newSong = cloneDeep(song)
        newSong.mediaURL = mediaURL
        dispatch(PlayerCreators.updatePlayerState(true))
        dispatch(PlayHistoryCreators.addSong(newSong))
        dispatch(PlayerCreators.selectSong(newSong))
      })
      .catch(() => {
        dispatch(PlayerCreators.updateLoadingPlayer(false))
      })
  }

// The playbar play button only resumes the loaded song, so a song restored
// from the last session or replayed after ending reports Play Song here
export const trackPendingPlay =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { song, pendingPlayTrigger } = getState().player as any
    if (!song?.trackid || !pendingPlayTrigger) return

    trackPlaySong(song, {
      source: PLAY_SOURCES.PLAYBAR,
      trigger: pendingPlayTrigger
    })
    dispatch(PlayerCreators.updatePendingPlayTrigger(null))
  }

export default { playSong, playSampleSongOnSafari, trackPendingPlay }
